import type { RuleMatch } from '../worker-configuration/automation/worker-automation.types';
import { formatCodeChip, IDenialCode } from './denial-code';

/**
 * The binding between a denials-table code group and the worker rule that acts on it (FE-75).
 *
 * FE-75 names the silent failure: if the dashboard and the worker disagree about what a group IS, the
 * page claims automation the engine never applies. This module is the one place a code group becomes a
 * rule match, so the dialog and every future entry point build the same thing.
 *
 * ## The match, and why it carries no RARC
 *
 * In the worker's rule model (enter-worker `PaymentRulePaymentMatchValue`), a payment match is
 * `carc.isMatch || rarc.isMatch`, and the RARC check is `containsAll(codes)` — true for an EMPTY list, so by
 * that model the `rarc: { codes: [] }` the Workers page sends would widen a rule to every claim. The platform
 * does not share the model: through its rule tester, on production claims with real remittances
 * (2026-09-14), a CO-197 rule with an empty RARC list matched none of the claims without CO-197, while rules
 * built from each claim's own codes matched. Leaving `rarc` out is correct either way — with no RARC the
 * match is the CARC alone — and it keeps this module independent of how an empty list is read.
 *
 * A denial's REMARK code is never added either. Under the worker model's OR it would also match claims that
 * carry the remark without the reason code — wider than the group, not narrower — and how the platform
 * reads a non-empty RARC list has not been checked.
 *
 * Groups are stored as full names (`CONTRACTUAL_OBLIGATIONS`): the matcher compares against the enum's
 * `toString()`, which returns the full name, and `IDenialCode.group` already is one.
 *
 * ## Why nothing here imports the rule helpers at runtime
 *
 * `worker-automation.utils` cannot load under the node jest config (an ES-module dependency in its import
 * chain). The dialog passes in the store's own name check and length limit instead of this file copying
 * them, so there is still one definition of each.
 */

/** The PAYMENT match for exactly one code group: its CARC group and reason code, nothing else. */
export const paymentMatchForDenialCode = (code: IDenialCode): RuleMatch => ({
    scope: 'PAYMENT',
    value: { carc: { group: [code.group], codes: [code.reasonCode] } } as RuleMatch['value'],
});

/** A PAYER match for the chosen payers, or null for "any payer" — an empty payer block fails validation. */
export const payerMatchForIds = (payerIds: string[]): RuleMatch | null => {
    const ids = payerIds.map(id => id.trim()).filter(Boolean);
    return ids.length > 0 ? { scope: 'PAYER', value: { payerIds: ids } } : null;
};

/** Every match block for a code group, in the order the rule stores them (all must match: AND). */
export const denialWorkerMatches = (code: IDenialCode, payerIds: string[]): RuleMatch[] => {
    const payer = payerMatchForIds(payerIds);
    return payer ? [paymentMatchForDenialCode(code), payer] : [paymentMatchForDenialCode(code)];
};

export interface IDenialWorkerRuleNameOptions {
    /** The store's own uniqueness check (rule names are unique across denial AND rejection rules). */
    isNameTaken: (name: string) => boolean;
    /** The store's own limit. */
    maxLength: number;
}

/**
 * A readable, unique rule name: `CO-197 · Any payer`, `CO-197 · 2 payers`.
 *
 * The name is load-bearing, not a label: when task grouping is on it is the task category. Taken names
 * get a counter, trimmed so the counter always survives the length limit.
 */
export const denialWorkerRuleName = (
    code: IDenialCode,
    payerIds: string[],
    { isNameTaken, maxLength }: IDenialWorkerRuleNameOptions,
): string => {
    const payers = payerMatchForIds(payerIds);
    const count = payers ? (payers.value as { payerIds: string[] }).payerIds.length : 0;
    const scope = count === 0 ? 'Any payer' : count === 1 ? '1 payer' : `${count} payers`;
    const base = `${formatCodeChip(code)} · ${scope}`.slice(0, maxLength).trimEnd();

    if (!isNameTaken(base)) {
        return base;
    }

    for (let n = 2; n < 1000; n += 1) {
        const suffix = ` ${n}`;
        const candidate = `${base.slice(0, maxLength - suffix.length).trimEnd()}${suffix}`;
        if (!isNameTaken(candidate)) {
            return candidate;
        }
    }

    return base;
};
